/* =========================================================
   UNMESS — Global JS
   Cart API, custom elements, scroll reveal, utilities.
   Vanilla, dependency-free, defer-loaded.
   ========================================================= */
(function () {
  'use strict';

  const routes = window.Unmess?.routes || {};
  const strings = window.Unmess?.strings || {};

  /* ---------- Small helpers ---------- */
  const on = (el, evt, fn, opts) => el && el.addEventListener(evt, fn, opts);

  /*
   * money(cents)
   * Interpola window.Unmess.moneyFormat per tutti i formati Shopify:
   *   {{amount}}                                  → 29.99
   *   {{amount_no_decimals}}                      → 29
   *   {{amount_with_comma_separator}}             → 29,99
   *   {{amount_no_decimals_with_comma_separator}} → 29
   * La regex cattura qualsiasi variante {{amount*}} in una sola passata.
   */
  const money = (cents) => {
    const fmt = window.Unmess?.moneyFormat || '{{amount}}';
    const amount = cents / 100;
    return fmt.replace(/\{\{\s*(amount[_a-z]*)\s*\}\}/g, (_, type) => {
      switch (type) {
        case 'amount_no_decimals':
          return Math.round(amount).toString();
        case 'amount_with_comma_separator':
          return amount.toFixed(2).replace('.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return Math.round(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        default: // 'amount'
          return amount.toFixed(2);
      }
    });
  };

  // Esponi globalmente per popup e altri moduli
  if (window.Unmess) window.Unmess.formatMoney = money;

  const publish = (name, detail) =>
    document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));

  /* ---------- Cart API (single source of truth) ---------- */
  const Cart = {
    _inflight: null,

    async _fetch(url, body) {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw data;
      return data;
    },

    async getState() {
      const res = await fetch(`${routes.cart_url || '/cart'}.js`, {
        headers: { Accept: 'application/json' }
      });
      return res.json();
    },

    async add(items) {
      const payload = Array.isArray(items) ? { items } : { items: [items] };
      const data = await this._fetch(`${routes.cart_add_url || '/cart/add'}.js`, payload);
      const cart = await this.getState();
      publish('cart:updated', { cart, added: data });
      return data;
    },

    async change(line, quantity) {
      // Serialize mutations to prevent race conditions.
      this._inflight = (this._inflight || Promise.resolve())
        .catch(() => {})
        .then(() => this._fetch(`${routes.cart_change_url || '/cart/change'}.js`, { line, quantity }));
      const cart = await this._inflight;
      publish('cart:updated', { cart });
      return cart;
    }
  };
  window.UnmessCart = Cart;

  /* =========================================================
     <cart-drawer>
     ========================================================= */
  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.overlay = this.querySelector('[data-drawer-overlay]');
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      on(this.overlay, 'click', () => this.close());
      on(this, 'click', (e) => { if (e.target.closest('[data-drawer-close]')) this.close(); });
      on(document, 'cart:updated', (e) => this.render(e.detail.cart));
      on(document, 'cart:open', () => this.open());
    }

    open() {
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this.onKeydown);
      const focusable = this.querySelector('[data-drawer-close]');
      if (focusable) focusable.focus({ preventScroll: true });
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this.onKeydown);
    }

    onKeydown(e) { if (e.key === 'Escape') this.close(); }

    async render(cart) {
      if (!cart) cart = await Cart.getState();
      const res = await fetch(`${window.location.pathname}?section_id=cart-drawer`);
      const text = await res.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const fresh = html.querySelector('cart-drawer [data-drawer-body]');
      const target = this.querySelector('[data-drawer-body]');
      if (fresh && target) target.innerHTML = fresh.innerHTML;
      const freshFoot = html.querySelector('cart-drawer .cart-drawer__foot');
      const oldFoot = this.querySelector('.cart-drawer__foot');
      if (freshFoot && oldFoot) oldFoot.replaceWith(freshFoot);
      else if (freshFoot && !oldFoot) this.querySelector('.cart-drawer__panel').appendChild(freshFoot);
      else if (!freshFoot && oldFoot) oldFoot.remove();
      this.dataset.count = cart.item_count;
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  /* =========================================================
     <cart-root>
     ========================================================= */
  class CartRoot extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this._busy = false;
      on(document, 'cart:updated', () => this.refresh());
    }
    async refresh() {
      if (!this.sectionId || this._busy) return;
      this._busy = true;
      try {
        const res = await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(this.sectionId)}`);
        const text = await res.text();
        const html = new DOMParser().parseFromString(text, 'text/html');
        const fresh = html.querySelector('cart-root');
        if (fresh) this.innerHTML = fresh.innerHTML;
      } catch (err) {
        console.error('[cart-root] refresh failed', err);
      } finally {
        this._busy = false;
      }
    }
  }
  customElements.define('cart-root', CartRoot);

  /* =========================================================
     <cart-item>
     ========================================================= */
  class CartItem extends HTMLElement {
    connectedCallback() {
      this.line = Number(this.dataset.line);
      this.querySelectorAll('[data-qty-btn]').forEach((btn) =>
        on(btn, 'click', () => this.step(Number(btn.dataset.qtyBtn)))
      );
      const input = this.querySelector('[data-qty-input]');
      on(input, 'change', () => this.set(Number(input.value)));
      on(this.querySelector('[data-remove]'), 'click', (e) => { e.preventDefault(); this.set(0); });
    }
    step(delta) {
      const input = this.querySelector('[data-qty-input]');
      this.set(Math.max(0, Number(input.value) + delta));
    }
    async set(qty) {
      this.setBusy(true);
      try { await Cart.change(this.line, qty); }
      catch (err) { console.error('[cart] change failed', err); this.setBusy(false); }
    }
    setBusy(state) {
      this.classList.toggle('is-busy', state);
      this.querySelectorAll('button, input').forEach((el) => (el.disabled = state));
    }
  }
  customElements.define('cart-item', CartItem);

  /* =========================================================
     <cart-count>
     ========================================================= */
  class CartCount extends HTMLElement {
    connectedCallback() {
      on(document, 'cart:updated', (e) => this.update(e.detail.cart.item_count));
    }
    update(count) {
      this.textContent = count;
      this.hidden = count < 1;
      this.classList.remove('pulse');
      void this.offsetWidth;
      this.classList.add('pulse');
    }
  }
  customElements.define('cart-count', CartCount);

  /* =========================================================
     <product-form>
     ========================================================= */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.submitBtn = this.querySelector('[type="submit"]');
      this.errorEl = this.querySelector('[data-form-error]');
      on(this.form, 'submit', (e) => this.onSubmit(e));
    }
    async onSubmit(e) {
      e.preventDefault();
      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;
      this.setLoading(true);
      this.clearError();
      const formData = new FormData(this.form);
      const item = { id: Number(formData.get('id')), quantity: Number(formData.get('quantity') || 1) };
      const props = {};
      for (const [key, value] of formData.entries()) {
        const m = key.match(/^properties\[(.+)\]$/);
        if (m) props[m[1]] = value;
      }
      if (Object.keys(props).length) item.properties = props;
      try {
        await Cart.add(item);
        this.flashSuccess();
        if (window.Unmess?.cartType === 'drawer') publish('cart:open');
        else window.location.href = routes.cart_url || '/cart';
      } catch (err) {
        this.showError(err?.description || strings.cartError || 'Could not add to cart.');
      } finally {
        this.setLoading(false);
      }
    }
    setLoading(state) {
      this.submitBtn.setAttribute('aria-disabled', String(state));
      this.submitBtn.classList.toggle('is-loading', state);
    }
    flashSuccess() {
      this.submitBtn.classList.add('is-success');
      setTimeout(() => this.submitBtn.classList.remove('is-success'), 1400);
    }
    clearError() { if (this.errorEl) { this.errorEl.hidden = true; this.errorEl.textContent = ''; } }
    showError(msg) { if (this.errorEl) { this.errorEl.hidden = false; this.errorEl.textContent = msg; } }
  }
  customElements.define('product-form', ProductForm);

  /* =========================================================
     <quick-add>
     ========================================================= */
  class QuickAdd extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector('button');
      this.variantId = this.dataset.variantId;
      on(this.btn, 'click', () => this.add());
    }
    async add() {
      if (this.btn.getAttribute('aria-disabled') === 'true') return;
      this.btn.setAttribute('aria-disabled', 'true');
      this.btn.classList.add('is-loading');
      try {
        await Cart.add({ id: Number(this.variantId), quantity: 1 });
        this.btn.classList.add('is-success');
        if (window.Unmess?.cartType === 'drawer') publish('cart:open');
        setTimeout(() => this.btn.classList.remove('is-success'), 1400);
      } catch (err) {
        console.error('[quick-add]', err);
      } finally {
        this.btn.setAttribute('aria-disabled', 'false');
        this.btn.classList.remove('is-loading');
      }
    }
  }
  customElements.define('quick-add', QuickAdd);

  /* =========================================================
     Cart toggle triggers
     ========================================================= */
  document.querySelectorAll('[data-cart-toggle]').forEach((el) =>
    on(el, 'click', (e) => {
      if (window.Unmess?.cartType !== 'drawer') return;
      e.preventDefault();
      publish('cart:open');
    })
  );

  /* =========================================================
     Scroll reveal
     ========================================================= */
  window.__unmessRevealReady = true;
  const reveals = document.querySelectorAll('.reveal');
  if (reveals.length && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    reveals.forEach((el) => io.observe(el));
  } else {
    reveals.forEach((el) => el.classList.add('is-visible'));
  }

  /* =========================================================
     <accordion-group>
     ========================================================= */
  class AccordionGroup extends HTMLElement {
    connectedCallback() {
      if (this.hasAttribute('data-single')) {
        this.querySelectorAll('details').forEach((d) =>
          on(d, 'toggle', () => {
            if (d.open) {
              this.querySelectorAll('details[open]').forEach((o) => { if (o !== d) o.open = false; });
            }
          })
        );
      }
    }
  }
  customElements.define('accordion-group', AccordionGroup);

  /* =========================================================
     <count-down>
     ========================================================= */
  class CountDown extends HTMLElement {
    connectedCallback() {
      this.deadline = new Date(this.dataset.deadline).getTime();
      this.parts = {
        d: this.querySelector('[data-cd="d"]'),
        h: this.querySelector('[data-cd="h"]'),
        m: this.querySelector('[data-cd="m"]'),
        s: this.querySelector('[data-cd="s"]')
      };
      if (isNaN(this.deadline)) return;
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    tick() {
      let diff = Math.max(0, this.deadline - Date.now());
      if (diff <= 0) { clearInterval(this.timer); this.classList.add('is-expired'); }
      const d = Math.floor(diff / 86400000); diff -= d * 86400000;
      const h = Math.floor(diff / 3600000); diff -= h * 3600000;
      const m = Math.floor(diff / 60000); diff -= m * 60000;
      const s = Math.floor(diff / 1000);
      const pad = (n) => String(n).padStart(2, '0');
      if (this.parts.d) this.parts.d.textContent = pad(d);
      if (this.parts.h) this.parts.h.textContent = pad(h);
      if (this.parts.m) this.parts.m.textContent = pad(m);
      if (this.parts.s) this.parts.s.textContent = pad(s);
    }
  }
  customElements.define('count-down', CountDown);

  /* =========================================================
     <variant-picker>
     ========================================================= */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.priceTarget = document.querySelector(this.dataset.priceTarget);
      this.forms = document.querySelectorAll(`form[data-product="${this.dataset.product}"]`);
      this.addEventListener('change', () => this.onChange());
    }
    get selectedOptions() {
      return Array.from(this.querySelectorAll('select, input[type="radio"]:checked')).map((el) => el.value);
    }
    onChange() {
      const opts = this.selectedOptions;
      const match = this.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      this.forms.forEach((form) => {
        const idInput = form.querySelector('input[name="id"]');
        const btn = form.querySelector('[type="submit"]');
        if (!match) {
          if (btn) {
            btn.disabled = true;
            const unavailLabel = btn.querySelector('.btn__label');
            if (unavailLabel) unavailLabel.textContent = strings.unavailable || 'Unavailable';
          }
          return;
        }
        if (idInput) idInput.value = match.id;
        if (btn) {
          btn.disabled = !match.available;
          const label = btn.querySelector('.btn__label');
          if (label) label.textContent = match.available
            ? (strings.addToCart || 'Add to cart')
            : (strings.soldOut || 'Sold out');
        }
      });
      if (match && this.priceTarget && window.Unmess?.renderPrice) {
        this.priceTarget.innerHTML = window.Unmess.renderPrice(match);
      }
      publish('variant:changed', { variant: match });
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* =========================================================
     <media-gallery>
     ========================================================= */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('[data-gallery-main]');
      this.querySelectorAll('[data-gallery-thumb]').forEach((thumb) =>
        on(thumb, 'click', () => this.select(thumb))
      );
    }
    select(thumb) {
      const id = thumb.dataset.mediaId;
      this.querySelectorAll('[data-gallery-slide]').forEach((s) =>
        s.toggleAttribute('hidden', s.dataset.mediaId !== id)
      );
      this.querySelectorAll('[data-gallery-thumb]').forEach((t) =>
        t.setAttribute('aria-current', String(t === thumb))
      );
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /* =========================================================
     <sticky-atc>
     ========================================================= */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const anchor = document.querySelector(this.dataset.watch || '#ProductForm');
      if (!anchor || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver(([entry]) => {
        this.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      }, { threshold: 0 });
      io.observe(anchor);
    }
  }
  customElements.define('sticky-atc', StickyAtc);
})();
